import type { FC, ReactNode } from 'react'
import type { Layout, OpenCard, Pose } from '@/components/riffle/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import {
    IMG_GRADE,
    N,
    PRODUCTS,
    TINTS,
    cardUrl,
    hiresUrl
} from '@/components/riffle/data'
import { clamp, smoothstep } from '@/components/riffle/math'
import {
    buildGlobe,
    fCarousel,
    resolvePose
} from '@/components/riffle/formations'
import DetailView from '@/components/riffle/detail-view'

// ─── §19 Constants ──────────────────────────────────────────
const PIN_DISTANCE = '+=620%'
const BROWSE_START = 0.8
const SPRING = 0.2
const PERSP = 1400
const NAV_SAFE = 34
const CAROUSEL_SCROLL_CARDS = 5
const AUTO_DRIFT = 0.003
const DRAG_GAIN = 0.0045
const DRAG_FRICTION = 0.93
const WHEEL_GAIN = 0.00045

const CARD_SHADOW =
    '0 2px 5px rgba(0,0,0,0.45), 0 30px 60px -16px rgba(0,0,0,0.66), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1.5px 2px rgba(0,0,0,0.34), inset 0 0 0 1px rgba(255,255,255,0.05)'

const computeLayout = (): Layout => {
    const W = window.innerWidth
    const H = window.innerHeight
    const mobile = W < 700
    const card = mobile
        ? clamp(W * 0.3, 104, 150)
        : clamp(Math.min(W, H) * 0.2, 92, 188)
    return { W, H, mobile, narrow: W < 900, card }
}

const emptyPose = (): Pose => ({ x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0, s: 1, o: 0 })

export const RiffleDeck: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const spacerRef = useRef<HTMLDivElement>(null)
    const sceneRef = useRef<HTMLDivElement>(null)
    const parallaxRef = useRef<HTMLDivElement>(null)
    const tintRef = useRef<HTMLDivElement>(null)
    const hintRef = useRef<HTMLDivElement>(null)

    const outerRefs = useRef<(HTMLDivElement | null)[]>([])
    const innerRefs = useRef<(HTMLDivElement | null)[]>([])
    const sheenRefs = useRef<(HTMLDivElement | null)[]>([])
    const labelRefs = useRef<(HTMLDivElement | null)[]>([])

    const openRef = useRef(false)
    const [open, setOpen] = useState<OpenCard | null>(null)
    const [reduced, setReduced] = useState(false)

    const handleClose = useCallback(() => {
        openRef.current = false
        setOpen(null)
    }, [])

    useEffect(() => {
        const section = sectionRef.current
        const spacerEl = spacerRef.current
        const parallax = parallaxRef.current
        const tintEl = tintRef.current
        const hintEl = hintRef.current
        if (!section || !spacerEl || !parallax || !tintEl || !hintEl) return

        const outers = outerRefs.current
        const inners = innerRefs.current
        const sheens = sheenRefs.current
        const labels = labelRefs.current

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        setReduced(reduce)

        let killed = false
        let L = computeLayout()
        let sphere = buildGlobe(L)

        // per-frame state
        const cur: Pose[] = PRODUCTS.map(emptyPose)
        let seeded = false
        let progress = 0
        let offset = 0
        let vel = 0
        let focus = 0
        let overCard = false
        let lastRound = NaN
        const par = { x: 0, y: 0, tx: 0, ty: 0 }
        const cursor = { x: -1, y: -1 }
        const tint = { r: TINTS[0][0], g: TINTS[0][1], b: TINTS[0][2] }
        const warmed = new Set<number>()
        const drag = { down: false, active: false, sx: 0, sy: 0, lastX: 0, moved: false }

        const interactive = (): boolean => progress >= BROWSE_START - 0.05

        const applySizes = (): void => {
            const c = L.card
            const radius = Math.round(c * 0.09)
            for (let i = 0; i < N; i++) {
                const o = outers[i]
                if (o) {
                    o.style.width = `${c}px`
                    o.style.height = `${c}px`
                    o.style.marginLeft = `${-c / 2}px`
                    o.style.marginTop = `${-c / 2}px`
                }
                const inn = inners[i]
                if (inn) inn.style.borderRadius = `${radius}px`
            }
        }

        const warm = (i: number): void => {
            if (warmed.has(i)) return
            warmed.add(i)
            const img = new Image()
            img.src = hiresUrl(i + 1)
        }

        const paintTint = (): void => {
            const mx = Math.max(tint.r, tint.g, tint.b, 1)
            const lift = clamp(170 / mx, 1, 2.6)
            const r = Math.min(255, tint.r * lift) | 0
            const g = Math.min(255, tint.g * lift) | 0
            const b = Math.min(255, tint.b * lift) | 0
            tintEl.style.background =
                `radial-gradient(ellipse 110% 65% at 50% -8%, rgba(${r},${g},${b},0.22) 0%, rgba(${r},${g},${b},0) 56%), ` +
                `radial-gradient(ellipse 90% 52% at 50% 108%, rgba(${r},${g},${b},0.15) 0%, rgba(${r},${g},${b},0) 60%)`
        }

        const writeCard = (i: number, p: Pose, labelFade: number): void => {
            const o = outers[i]
            if (o) {
                o.style.transform =
                    `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, ${p.z.toFixed(2)}px) ` +
                    `rotateX(${p.rx.toFixed(2)}deg) rotateY(${p.ry.toFixed(2)}deg) rotateZ(${p.rz.toFixed(2)}deg) ` +
                    `scale(${p.s.toFixed(3)})`
                o.style.opacity = `${p.o}`
                o.style.zIndex = `${Math.round(2000 + p.z)}`
            }
            const sh = sheens[i]
            if (sh) {
                const k = clamp(p.ry / 54, -1, 1)
                sh.style.transform = `translateX(${(k * 42).toFixed(2)}%)`
                sh.style.opacity = `${0.08 + Math.abs(k) * 0.26}`
            }
            const lab = labels[i]
            if (lab) {
                const persp = PERSP / (PERSP - p.z)
                const projX = L.W / 2 + p.x * persp
                const projY = L.H / 2 + NAV_SAFE + p.y * persp - (L.card / 2) * p.s * persp - 16
                lab.style.transform = `translate3d(${projX.toFixed(2)}px, ${projY.toFixed(2)}px, 0) translate(-50%, -100%)`
                lab.style.opacity = `${labelFade * p.o * clamp((p.s - 0.55) / 0.45, 0, 1)}`
            }
        }

        const hitTest = (x: number, y: number, minO: number): number => {
            if (x < 0 || y < 0) return -1
            let best = -1
            let bestZ = -Infinity
            for (let i = 0; i < N; i++) {
                if (cur[i].o < minO) continue
                const el = inners[i]
                if (!el) continue
                const r = el.getBoundingClientRect()
                if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
                    if (cur[i].z > bestZ) {
                        bestZ = cur[i].z
                        best = i
                    }
                }
            }
            return best
        }

        const openIndex = (i: number): void => {
            const el = inners[i]
            if (!el) return
            const r = el.getBoundingClientRect()
            openRef.current = true
            setOpen({ index: i, rect: { x: r.left, y: r.top, w: r.width, h: r.height } })
        }

        applySizes()

        // ─── Reduced motion: snap to the carousel rest state, render once ───
        if (reduce) {
            for (let i = 0; i < N; i++) {
                const p = fCarousel(i, L, 0)
                Object.assign(cur[i], p)
                writeCard(i, p, 1)
                const inn = inners[i]
                if (inn) {
                    inn.style.opacity = '1'
                    inn.style.filter = 'none'
                }
            }
            parallax.style.transform = `translateY(${NAV_SAFE}px)`
            paintTint()
            hintEl.style.opacity = '0'

            const onStaticMove = (e: PointerEvent): void => {
                section.style.cursor = hitTest(e.clientX, e.clientY, 0.3) >= 0 ? 'pointer' : 'default'
            }
            const onStaticClick = (e: MouseEvent): void => {
                if (openRef.current) return
                const i = hitTest(e.clientX, e.clientY, 0.3)
                if (i >= 0) openIndex(i)
            }
            const onResizeStatic = (): void => {
                L = computeLayout()
                applySizes()
                sphere = buildGlobe(L)
                for (let i = 0; i < N; i++) {
                    const p = fCarousel(i, L, 0)
                    Object.assign(cur[i], p)
                    writeCard(i, p, 1)
                }
            }

            section.addEventListener('pointermove', onStaticMove)
            section.addEventListener('click', onStaticClick)
            window.addEventListener('resize', onResizeStatic)

            return () => {
                killed = true
                section.removeEventListener('pointermove', onStaticMove)
                section.removeEventListener('click', onStaticClick)
                window.removeEventListener('resize', onResizeStatic)
            }
        }

        // ─── §12 Scroll pinning ───
        gsap.registerPlugin(ScrollTrigger)
        const st = ScrollTrigger.create({
            trigger: section,
            start: 'top top',
            end: PIN_DISTANCE,
            pin: true,
            // Adopt the wrapper React already rendered. Left to itself,
            // ScrollTrigger creates a spacer and moves the section inside it,
            // which leaves React holding a stale parent — unmounting the page
            // then throws on removeChild.
            pinSpacer: spacerEl,
            pinSpacing: true,
            anticipatePin: 1,
            onUpdate: (self) => {
                progress = self.progress
            }
        })

        // ─── §13 Entrance bloom (gated on fonts) ───
        document.fonts.ready.then(() => {
            if (killed) return
            const list = inners.filter(Boolean) as HTMLDivElement[]
            gsap.set(list, { opacity: 0, scale: 0.62, filter: 'blur(10px)' })
            gsap.to(list, {
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
                duration: 1.05,
                ease: 'power4.out',
                stagger: { each: 0.04, from: 'center' },
                delay: 0.15
            })
        })

        // ─── §11 Render loop ───
        let raf = 0
        const frame = (): void => {
            raf = requestAnimationFrame(frame)
            if (openRef.current) return // frozen while a detail is open

            const P = progress
            const inCarousel = P >= BROWSE_START - 0.02
            const browseT = clamp((P - 0.8) / 0.2, 0, 1)

            // 11.2 carousel offset
            if (!drag.active) {
                offset += vel
                vel *= DRAG_FRICTION
                if (Math.abs(vel) < 0.00005) vel = 0
                const settling = inCarousel && vel !== 0 && Math.abs(vel) < 0.01
                if (settling) {
                    const ff = browseT * CAROUSEL_SCROLL_CARDS + offset
                    const snap = Math.round(ff)
                    offset += (snap - ff) * 0.12
                    if (Math.abs(snap - ff) < 0.0015) vel = 0
                } else if (inCarousel && !overCard) {
                    offset += AUTO_DRIFT
                }
            }
            focus = browseT * CAROUSEL_SCROLL_CARDS + offset

            // 11.3 preload newly centred card
            const fr = Math.round(focus)
            if (inCarousel && fr !== lastRound) {
                lastRound = fr
                warm(((fr % N) + N) % N)
            }

            // 11.4 cursor parallax (whole deck)
            par.x += (par.tx - par.x) * 0.06
            par.y += (par.ty - par.y) * 0.06
            parallax.style.transform =
                `translateY(${NAV_SAFE}px) rotateX(${par.x.toFixed(2)}deg) rotateY(${par.y.toFixed(2)}deg)`

            // hover hit-test + cursor affordance
            if (inCarousel && !drag.active) {
                const over = hitTest(cursor.x, cursor.y, 0.45)
                overCard = over >= 0
                if (overCard) warm(over)
                section.style.cursor = overCard ? 'pointer' : 'grab'
            } else if (drag.active) {
                section.style.cursor = 'grabbing'
            } else {
                overCard = false
                section.style.cursor = 'default'
            }

            // 11.5–11.7 per card spring + write
            const labelFade = smoothstep((P - 0.8) / 0.08)
            for (let i = 0; i < N; i++) {
                const t = resolvePose(i, P, L, focus, sphere)
                const c = cur[i]
                if (!seeded || Math.abs(t.x - c.x) > L.W) {
                    c.x = t.x
                    c.y = t.y
                    c.z = t.z
                    c.rx = t.rx
                    c.ry = t.ry
                    c.rz = t.rz
                    c.s = t.s
                    c.o = t.o
                } else {
                    c.x += (t.x - c.x) * SPRING
                    c.y += (t.y - c.y) * SPRING
                    c.z += (t.z - c.z) * SPRING
                    c.rx += (t.rx - c.rx) * SPRING
                    c.ry += (t.ry - c.ry) * SPRING
                    c.rz += (t.rz - c.rz) * SPRING
                    c.s += (t.s - c.s) * SPRING
                    c.o += (t.o - c.o) * SPRING
                }
                writeCard(i, c, labelFade)
            }
            seeded = true

            // scroll hint (visible only at the very top)
            hintEl.style.opacity = `${clamp(1 - P / 0.03, 0, 1)}`

            // 11.8 ambient tint
            const ti = (((Math.round(focus) % N) + N) % N)
            const tg = TINTS[ti]
            tint.r += (tg[0] - tint.r) * 0.045
            tint.g += (tg[1] - tint.g) * 0.045
            tint.b += (tg[2] - tint.b) * 0.045
            paintTint()
        }
        raf = requestAnimationFrame(frame)

        // ─── §15 Interactions ───
        const onMove = (e: PointerEvent): void => {
            cursor.x = e.clientX
            cursor.y = e.clientY
            par.ty = (e.clientX / L.W - 0.5) * 5
            par.tx = (0.5 - e.clientY / L.H) * 5
            if (!drag.down) return
            const totX = e.clientX - drag.sx
            const totY = e.clientY - drag.sy
            if (!drag.active) {
                if (Math.abs(totX) < 10 && Math.abs(totY) < 10) return
                if (Math.abs(totY) > Math.abs(totX)) {
                    drag.down = false // vertical intent → let the page scroll
                    return
                }
                drag.active = true
                try {
                    section.setPointerCapture(e.pointerId)
                } catch {}
            }
            const dx = e.clientX - drag.lastX
            drag.lastX = e.clientX
            drag.moved = true
            const delta = -dx * DRAG_GAIN
            offset += delta
            vel = delta
        }

        const onDown = (e: PointerEvent): void => {
            if (openRef.current || !interactive()) return
            drag.down = true
            drag.active = false
            drag.moved = false
            drag.sx = e.clientX
            drag.sy = e.clientY
            drag.lastX = e.clientX
            vel = 0
        }

        const onUp = (e: PointerEvent): void => {
            if (drag.down && !drag.active) {
                const totX = Math.abs(e.clientX - drag.sx)
                const totY = Math.abs(e.clientY - drag.sy)
                if (totX < 10 && totY < 10 && interactive() && !openRef.current) {
                    const i = hitTest(e.clientX, e.clientY, 0.3)
                    if (i >= 0) openIndex(i)
                }
            }
            drag.down = false
            drag.active = false
            try {
                section.releasePointerCapture(e.pointerId)
            } catch {}
        }

        const onLeave = (): void => {
            par.tx = 0
            par.ty = 0
            cursor.x = -1
            cursor.y = -1
            overCard = false
        }

        const onWheel = (e: WheelEvent): void => {
            if (openRef.current) {
                e.preventDefault() // lock page scroll while a detail is open
                return
            }
            if (progress >= BROWSE_START + 0.1 && e.deltaY > 0) {
                e.preventDefault()
                vel = clamp(vel + e.deltaY * WHEEL_GAIN, -0.6, 0.6)
            }
        }

        const onResize = (): void => {
            L = computeLayout()
            applySizes()
            sphere = buildGlobe(L)
            ScrollTrigger.refresh()
        }

        section.addEventListener('pointermove', onMove)
        section.addEventListener('pointerdown', onDown)
        window.addEventListener('pointerup', onUp)
        section.addEventListener('pointerleave', onLeave)
        window.addEventListener('wheel', onWheel, { passive: false })
        window.addEventListener('resize', onResize)

        return () => {
            killed = true
            cancelAnimationFrame(raf)
            st.kill()
            section.removeEventListener('pointermove', onMove)
            section.removeEventListener('pointerdown', onDown)
            window.removeEventListener('pointerup', onUp)
            section.removeEventListener('pointerleave', onLeave)
            window.removeEventListener('wheel', onWheel)
            window.removeEventListener('resize', onResize)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            {/* React owns this node so it stays a real child of its parent.
                GSAP adopts it as the pin spacer instead of inserting its own
                and re-parenting the section out from under React. */}
            <div ref={spacerRef}>
                <section
                    ref={sectionRef}
                    className='riffle-stage'
                    style={{
                        position: 'relative',
                        height: '100vh',
                        width: '100%',
                        overflow: 'hidden',
                        touchAction: 'pan-y'
                    }}
                >
                    {/* 1 · ambient tint */}
                    <div ref={tintRef} aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

                    {/* 2 · overhead key light */}
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            background:
                                'radial-gradient(ellipse 80% 50% at 50% -12%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 58%)'
                        }}
                    />

                    {/* 3 · floor glow */}
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            insetInline: 0,
                            bottom: 0,
                            height: '50%',
                            pointerEvents: 'none',
                            background:
                                'radial-gradient(ellipse 62% 130% at 50% 138%, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 66%)'
                        }}
                    />

                    {/* 4 · wordmark — now carried by the site header (same position,
                           same type), so the stage does not draw a second one. */}

                    {/* 5 · 3D scene (perspective on a static wrapper) */}
                    <div
                        ref={sceneRef}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            perspective: `${PERSP}px`,
                            perspectiveOrigin: '50% 50%'
                        }}
                    >
                        <div
                            ref={parallaxRef}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                // both axes must centre: justify-items (place-items) is ignored
                                // on flex, so the 0×0 anchor would otherwise sit at the left edge
                                alignItems: 'center',
                                justifyContent: 'center',
                                transformStyle: 'preserve-3d',
                                willChange: 'transform'
                            }}
                        >
                            <div style={{ position: 'relative', width: 0, height: 0, transformStyle: 'preserve-3d' }}>
                                {PRODUCTS.map((p, i) => (
                                    <div
                                        key={i}
                                        ref={(el) => {
                                            outerRefs.current[i] = el
                                        }}
                                        style={{
                                            position: 'absolute',
                                            left: '50%',
                                            top: '50%',
                                            transformStyle: 'preserve-3d',
                                            backfaceVisibility: 'hidden',
                                            pointerEvents: 'none',
                                            willChange: 'transform, opacity'
                                        }}
                                    >
                                        <div
                                            ref={(el) => {
                                                innerRefs.current[i] = el
                                            }}
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '100%',
                                                overflow: 'hidden',
                                                background: '#15151a',
                                                opacity: 0,
                                                boxShadow: CARD_SHADOW,
                                                willChange: 'opacity, transform, filter'
                                            }}
                                        >
                                            { /* eslint-disable-next-line @next/next/no-img-element */ }
                                            <img
                                                src={cardUrl(i + 1)}
                                                alt={`${p.title} — ${p.artist}`}
                                                loading={i < 6 ? 'eager' : 'lazy'}
                                                draggable={false}
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: IMG_GRADE
                                                }}
                                            />
                                            <div
                                                ref={(el) => {
                                                    sheenRefs.current[i] = el
                                                }}
                                                aria-hidden
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    pointerEvents: 'none',
                                                    background:
                                                        'linear-gradient(105deg, transparent 34%, rgba(255,255,255,0.5) 50%, transparent 66%)',
                                                    mixBlendMode: 'overlay',
                                                    opacity: 0
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 6 · billboarded name labels */}
                    <div style={{ position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'none' }}>
                        {PRODUCTS.map((p, i) => (
                            <div
                                key={i}
                                ref={(el) => {
                                    labelRefs.current[i] = el
                                }}
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    opacity: 0,
                                    textAlign: 'center',
                                    willChange: 'transform, opacity'
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: 'var(--font-geist-sans)',
                                        fontSize: '12.5px',
                                        fontWeight: 500,
                                        letterSpacing: '0.01em',
                                        color: '#fff',
                                        textShadow: '0 2px 12px rgba(0,0,0,0.7)',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {p.title}
                                </div>
                                <div
                                    style={{
                                        fontFamily: 'var(--font-geist-mono)',
                                        fontSize: '8.5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.28em',
                                        opacity: 0.55,
                                        marginTop: 2,
                                        color: '#fff',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {p.artist}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 7 · scroll hint */}
                    <div
                        ref={hintRef}
                        className='absolute flex flex-col items-center'
                        style={{ bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 30, pointerEvents: 'none' }}
                    >
                        <span
                            className='font-mono uppercase'
                            style={{ fontSize: 10, letterSpacing: '0.42em', opacity: 0.55 }}
                        >
                            Scroll
                        </span>
                        <span style={{ width: 1, height: 28, background: 'currentColor', opacity: 0.3, marginTop: 8 }} />
                    </div>

                    {/* 8 · cinema vignette */}
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 20,
                            pointerEvents: 'none',
                            background:
                                'radial-gradient(ellipse 92% 82% at 50% 50%, rgba(0,0,0,0) 56%, rgba(0,0,0,0.28) 100%)'
                        }}
                    />
                </section>
            </div>

            {/* 9 · detail overlay */}
            {open && (
                <DetailView
                    product={PRODUCTS[open.index]}
                    index={open.index}
                    total={N}
                    origin={open.rect}
                    reducedMotion={reduced}
                    onClose={handleClose}
                />
            )}
        </>
    )
}

export default RiffleDeck
